import React, { useState, useEffect, useRef, Suspense } from 'react';
// Libraries
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import routes from '../routes/routes';
import { API, graphqlOperation } from 'aws-amplify';
import { onUpdateCourseStatus } from '../graphql/subscriptions/myAccountSubscriptions';
// Components
import LoadingScreen from '../components/LoadingScreen/loadingScreenView';
import ErrorHandler from '../containers/ErrorHandler/errorHandlerContainer';
import SimpleModal from '../components/Modal/SimpleModal/simpleModalView';
// Modal
import HomeLoadingScreen from '../containers/Modal/homeLoadingScreenContainer';

const MainLayout = props => {
  const {
    // States
    availableCourses,
    courseData,
    loadingHadler,
    loadingScreen,
    openDisabledModal,
    percentageLoadingScreen,
    selectedCourseId,
    studentData,
    submitedOpenDisabledCourseModal,
    // Functions
    getCourseData,
    handleCatchError,
    setNotification,
    setOpenDisabledCourseModal,
    setPercentageLoadingScreen,
    setSelectedCourseAction,
    setSubmitOpenDisableCourseModal,
    setValueTab,
    signOutUser,
    startLoading,
    switchCourse,
    updateUserInCognito
  } = props;
  const [loadRoutes, setLoadRoutes] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  let subscription = useRef(null);

  useEffect(() => {
    window.analytics.page({
      url: window.location.href,
      path: window.location.pathname
    });
    window.analytics.track('Pageview: ' + window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(async () => {
    if (studentData && studentData.id) validateUser();
    await analyzeUrl();
    setLoadRoutes(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (courseData && courseData.status && courseData.status !== 'ACTIVE') {
      if (location.pathname !== '/chooseCourse') setOpenDisabledCourseModal(true);
    } else setOpenDisabledCourseModal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData]);

  useEffect(() => {
    subscription.current = API.graphql(graphqlOperation(onUpdateCourseStatus)).subscribe({
      next:
        //get courseData after a is edited from creators app
        // eslint-disable-next-line
        ({ value }) => {
          const courseIdEdited =
            value && value.data && value.data.onUpdateCourse && value.data.onUpdateCourse.id
              ? value.data.onUpdateCourse.id
              : null;
          if (selectedCourseId && courseIdEdited && selectedCourseId === courseIdEdited) {
            getCourseData({ courseId: courseIdEdited });
          }
        },
      error: error => error !== '' && handleCatchError(error, 'mainLayout')
    });
    // Stop receiving data updates from the subscription
    return () => {
      subscription.current.unsubscribe();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyzeUrl = async () => {
    const pathname = window?.location?.pathname ? window.location.pathname : '';
    const urlParams = new URLSearchParams(window.location.search);
    /* 
    When the user clicks on a link from an email,
    /myAccount is appended to the url in that case, redirect to My Account view
    */
    if (pathname && pathname === '/myAccount') setValueTab(2);
    const refreshToken = urlParams.get('rtoken');
    const userPoolId = urlParams.get('UserPoolId');
    const clientId = urlParams.get('ClientId');
    const courseId = urlParams.get('courseId');
    const freeAccess = urlParams.get('AllowFreeAccess');
    const coursePreview = urlParams.get('coursePreview');
    if (refreshToken && userPoolId && clientId) {
      //No Context Scenario
      if (location.pathname === '/') startLoading();
      localStorage.setItem('RefreshToken', JSON.stringify(refreshToken));
      const now = new Date();
      localStorage.setItem('token_timestamp', now.toISOString());
      localStorage.setItem('ClientId', JSON.stringify(clientId));
      localStorage.setItem('UserPoolId', JSON.stringify(userPoolId));
      localStorage.setItem('NoContextUser', 'true');
    }
    if (courseData) localStorage.removeItem('NoContextUser');
    if (courseId && freeAccess) {
      localStorage.setItem('FreeAccessLink', 'true');
      localStorage.removeItem('coursePreview');
    }
    if (courseId && coursePreview) {
      localStorage.setItem('coursePreview', 'true');
      localStorage.removeItem('FreeAccessLink');
    }
    if (courseId && coursePreview) {
      let query = '?courseId=' + courseId;
      //Course PreviewFlag
      navigate('/newAccount' + query);
    }
    await currentAuthenticatedUser();
  };

  const validateUser = () => {
    const storedTimestamp = localStorage.getItem('token_timestamp');
    if (storedTimestamp) {
      const storedDate = new Date(storedTimestamp);
      const now = new Date();
      const hoursDiff = (now - storedDate) / (1000 * 60 * 60);
      if (hoursDiff >= 2) {
        updateUserInCognito({ operation: 'ValidateResfreshToken' });
        const now = new Date();
        localStorage.setItem('token_timestamp', now.toISOString());
      }
    } else {
      signOutUser({ userId: studentData?.id });
      setNotification({
        message: 'Your session has ended. Please log in again.',
        severity: 'warning'
      });
    }
  };

  const currentAuthenticatedUser = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    let refreshToken = JSON.parse(localStorage.getItem('RefreshToken'));
    let UserPoolId = JSON.parse(localStorage.getItem('UserPoolId'));
    let ClientId = JSON.parse(localStorage.getItem('ClientId'));
    let courseId;
    let allowFreeAccess;
    let coursePreview;
    let createAccount;
    if (urlParams) {
      for (let value of urlParams.keys()) {
        const inLower = value.toLowerCase();
        if (inLower === 'allowfreeaccess') allowFreeAccess = urlParams.get(value);
        if (inLower === 'courseid' || inLower === 'cid') courseId = urlParams.get(value);
        if (inLower === 'coursepreview') coursePreview = urlParams.get(value);
        if (inLower === 'createaccount') createAccount = urlParams.get(value);
      }
    }
    if (refreshToken && UserPoolId && ClientId) {
      /* empty */
    } else {
      // If the user is not logged in, redirect to login page
      // If the user came from an invitation link, redirect to login with the params in the url
      const queryStart = window.location.href.indexOf('?');
      if (queryStart !== -1) {
        const queryEnd = window.location.href.length;
        const query = window.location.href.slice(queryStart, queryEnd);
        if (location.pathname !== '/newAccount' && location.pathname !== '/signUp') {
          if (location.pathname === '/login' && createAccount && createAccount === 'false') {
            navigate('/login');
            setNotification({
              message:
                'You have cancelled the course subscription. You can try again with the previous link or if you already have an account you can log in.',
              severity: 'error'
            });
          } else {
            navigate('/login' + query);
          }
        } else {
          if (courseId && !allowFreeAccess && !coursePreview) {
            navigate('/newAccount' + query);
          }
        }
      } else {
        if (location.pathname !== '/login' && location.pathname !== '/newAccount') {
          navigate('/login');
        }
      }
    }
  };

  const getRoutes = () => {
    const validRoute = routes;
    return validRoute.map((prop, key) => {
      if (prop && prop.layout === '/') {
        return (
          <Route exact path={prop.layout + prop.path} element={<prop.component />} key={key} />
        );
      }
      return null;
    });
  };

  const enableLoadingScreen = () => {
    const query = new URLSearchParams(window.location.search);
    const isSignUp = location && location.pathname === '/newAccount' && !query.get('createAccount');
    const showScreen = loadingScreen || query.get('success') ? true : false;
    if (showScreen) {
      return (
        <HomeLoadingScreen
          percentage={percentageLoadingScreen}
          setPercentage={setPercentageLoadingScreen}
          userEmail=""
          open={showScreen}
          isSignUp={isSignUp}
        />
      );
    }
  };

  return (
    <>
      {loadingHadler > 0 && <LoadingScreen />}
      <Suspense fallback={<LoadingScreen />}>
        {enableLoadingScreen()}
        <Routes>{loadRoutes && getRoutes()}</Routes>
        <ErrorHandler />

        {openDisabledModal && courseData && courseData.activeSubscription ? (
          <SimpleModal
            open={openDisabledModal && !submitedOpenDisabledCourseModal}
            title={'The course has been disabled'}
            subtitle={'You will be able to continue studying until your subscription ends'}
            acceptBtnText={'Got it'}
            handleOnSubmit={() => {
              setOpenDisabledCourseModal(false);
              setSubmitOpenDisableCourseModal(true);
            }}
            blockClose
          />
        ) : (
          <SimpleModal
            open={openDisabledModal}
            title={'The course has been disabled'}
            subtitle={
              availableCourses && availableCourses.length > 0
                ? 'Please choose another course or log out'
                : 'Please log out'
            }
            acceptBtnText={'Log out'}
            cancelBtnText={
              availableCourses && availableCourses.length > 0 ? 'Choose another course' : null
            }
            handleOnSubmit={() => {
              setOpenDisabledCourseModal(false);
              localStorage.removeItem('persist:root');
              navigate('/login');
              signOutUser({ userId: studentData?.id });
            }}
            handleOnClose={() => {
              setOpenDisabledCourseModal(false);
              switchCourse();
              setSelectedCourseAction(null);
              navigate('/chooseCourse');
            }}
            blockClose
          />
        )}
      </Suspense>
    </>
  );
};

export default MainLayout;
